// @flow
import React from 'react'
import I18n from 'i18n-react'
import { humanizer } from 'devx-js-utilities'
import Immutable from 'seamless-immutable'

// core
import { WEB_TYPES_KEYS } from '../Redux/SettingsRedux'
import moment from '../Moment'

// components
import { FieldForm, PatchSpecification } from '../../Components/'
import { ColumnTypePriceIncludes } from '../../Components/_quoteComponents/'

// types
import type { InsuranceCompanyType } from '../Types/InsuranceCompanyTypes'
import type { TariffContentWithKeyType } from '../Types/TariffsContentType'
import type { ProductContent } from '../Types/ProductContent'
import type { CoveragePriceType } from '../Redux/Types/QuoteStateType'
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type QuoteInstance from '../Controllers/QuoteInstance'

type CarSummaryPanelProps = {
  addedCoverages?: Object,
  content?: ProductContent,
  coveragePrice?: CoveragePriceType,
  debug: boolean,
  hidePriceSummaryKeys: ?Array<string>,
  hideSummaryInfo: boolean,
  insuranceCompany: InsuranceCompanyType,
  insuranceStart?: string,
  isBackOffice: boolean,
  onChangeValue: Function,
  onGoBack?: () => void,
  pricePerPeriod?: number,
  quoteContent: ProductContent,
  selectedQuote: QuoteInstance,
  specification?: ExpandedSpecificationDTO,
  supplementaryInsurances: Object,
  tariffContent: TariffContentWithKeyType,
  templateVariables?: Object,
  userData: Object,
  validationErrors: Object,
  webType: number,
}

const CarSummaryPanel = (props: CarSummaryPanelProps) => {
  if (!props.tariffContent || !Object.keys(props.tariffContent).length || !Object.keys(props.quoteContent).length) {
    return null
  }

  let cicData = false
  let tariffLimits = {}
  if (props.tariffContent.cic && props.tariffContent.cic.properties) {
    cicData = true
    tariffLimits = props.tariffContent.cic.properties.limits
  }

  let customIcon = ''
  Object.keys(props.tariffContent).map((tariffKey) => {
    if (
      props.tariffContent[tariffKey].icon &&
      props.tariffContent[tariffKey].icon[WEB_TYPES_KEYS[props.webType || 0]]
    ) {
      customIcon = props.tariffContent[tariffKey].icon[WEB_TYPES_KEYS[props.webType || 0]]
    }
  })

  if (!tariffLimits) {
    return null
  }

  let priceNumber = props.pricePerPeriod || props.selectedQuote.price
  if (!props.pricePerPeriod && props.coveragePrice && props.coveragePrice.fullPrice) {
    priceNumber = props.coveragePrice.fullPrice
  }
  const price = `${humanizer.csNumber(priceNumber)} ${I18n.translate('general.currency')}`
  const paymentPeriod = props.userData.paymentPeriod

  const supplementaryInsurancesLength = Object.keys(props.supplementaryInsurances).filter((i) => i !== 'seats').length
  const addedCoverages = Immutable(props.addedCoverages)

  return (
    <div className="container">
      <div className="bg-default-bg p-2 p-md-3">
        {!props.hideSummaryInfo && (
          <>
            <div className="row align-items-center">
              <div className="col-12 mb-2 mb-lg-0 text-center text-lg-left">
                <strong className="h3">{I18n.translate('general.chosenInsurance')}</strong>
              </div>
              <div className="col-12 col-lg-1 mb-2 mb-lg-0">
                <div className="bg-transparent bg-lg-light border-lg-default d-inline-block">
                  <img
                    alt={props.insuranceCompany.name}
                    src={customIcon ? customIcon : props.insuranceCompany.webLogo}
                    width={60}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-5 pl-lg-0 mb-2 mb-lg-0 order-2 order-lg-0 mt-3 mt-lg-0">
                <p className="m-0 ml-2 small">
                  {props.selectedQuote.calculatorName.includes('Suri')
                    ? `${I18n.translate('general.productInCooperation')} `
                    : ''}
                  {props.insuranceCompany.name}
                </p>
                <p className="m-0 ml-2 small">{props.insuranceCompany.fullInfo}</p>
              </div>
              <div className="col-6 col-lg-3 text-lg-center">
                {props.tariffContent.tpl && [
                  <strong className="lead" key="tpl-tariff-tittle">
                    <strong>{I18n.translate('general.tpl')}</strong>
                  </strong>,
                  <br key="tpl-tariff-br" />,
                  <strong className="lead" key="tpl-tariff-name">
                    {props.tariffContent.tpl.name[WEB_TYPES_KEYS[props.webType || 0]]}
                  </strong>,
                ]}
                {Object.keys(props.tariffContent).length > 1 && <br />}
                {props.tariffContent.cic && [
                  <strong className="lead" key="cic-tariff-tittle">
                    <strong>{I18n.translate('general.cic')}</strong>
                  </strong>,
                  <br key="cic-tariff-br" />,
                  <strong className="lead" key="cic-tariff-name">
                    {props.tariffContent.cic.name[WEB_TYPES_KEYS[props.webType || 0]]}
                  </strong>,
                ]}
              </div>
              <div className="col-6 col-lg-3 text-right text-lg-left">
                {props.tariffContent.tpl && [
                  <br key="tpl-tariff-limits-br" />,
                  <strong className="lead" key="tpl-tariff-limits">
                    {props.tariffContent.tpl.properties.limits.health}/
                    {props.tariffContent.tpl.properties.limits.property}&nbsp;mil.&nbsp;
                    {I18n.translate('general.currency')}
                  </strong>,
                ]}
                {Object.keys(props.tariffContent).length > 1 && <br />}
                {cicData && [
                  <br key="cic-tariff-limits-br" />,
                  <strong className="lead" key="cic-tariff-limits">
                    {typeof tariffLimits.percent !== 'undefined' && <span>{`${tariffLimits.percent}%`},&nbsp;</span>}
                    {tariffLimits.value && (
                      <span>
                        {typeof tariffLimits.percent !== 'undefined' ? 'min.' : ''}&nbsp;
                        {humanizer.csNumber(tariffLimits.value)}&nbsp;
                        {I18n.translate('general.currency')}
                      </span>
                    )}
                  </strong>,
                ]}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-6 mb-2">
                <strong className="d-block mb-2 lead">{I18n.translate('general.insuranceIncludes')}:</strong>
                {props.tariffContent.tpl && (
                  <ColumnTypePriceIncludes
                    data={props.tariffContent.tpl.properties}
                    hidePriceSummaryKeys={props.hidePriceSummaryKeys}
                    identifyObject={{ type: props.userData.type }}
                    templateVariables={props.templateVariables}
                  />
                )}
                {props.tariffContent.cic && (
                  <ColumnTypePriceIncludes
                    data={props.tariffContent.cic.properties}
                    hidePriceSummaryKeys={props.hidePriceSummaryKeys}
                    identifyObject={{ type: props.userData.type }}
                    removeDuplicatesFrom={props.tariffContent.tpl ? props.tariffContent.tpl.properties : false}
                    templateVariables={props.templateVariables}
                  />
                )}
              </div>
              {!props.isBackOffice && (
                <div className="col-md-6 mb-2">
                  <div className="row mt-2 pr-4" key="car-summary-properties">
                    <div className="col-12 mb-2">
                      <strong className="d-block mb-2 lead">{I18n.translate('general.chosenSupInsurance')}:</strong>
                      {supplementaryInsurancesLength === 0 && <span>{I18n.translate('general.none')}</span>}
                    </div>
                    {supplementaryInsurancesLength > 0 &&
                      Object.keys(props.supplementaryInsurances).map((supplementaryInsuranceKey) => {
                        const insuranceContent = props.quoteContent.components[supplementaryInsuranceKey]
                        let fieldKey = supplementaryInsuranceKey
                        if (
                          props.quoteContent.fieldNames[
                            `${supplementaryInsuranceKey}${props.selectedQuote.calculatorName}`
                          ]
                        ) {
                          fieldKey = `${supplementaryInsuranceKey}${props.selectedQuote.calculatorName}`
                        }

                        if (!insuranceContent) {
                          return null
                        }

                        return (
                          <div
                            className="col-6 col-md-4 col-lg-3 py-2"
                            key={`car-summary-sup-insurance-${supplementaryInsuranceKey}`}
                          >
                            <div className="row align-items-center h-100">
                              <div className="col-4 text-center">
                                {insuranceContent.icon && <span className={`${insuranceContent.icon} h1`} />}
                              </div>
                              <div className="col-8 pl-1">
                                <span>{props.quoteContent.fieldNames[fieldKey]}</span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
            {props.isBackOffice && (
              <div className="row">
                <div className="col-md-6 mb-2">
                  <div className="row mt-2 pr-4" key="car-summary-properties">
                    <div className="col-12 mb-2">
                      <strong className="d-block mb-2 lead">{I18n.translate('general.chosenSupInsurance')}:</strong>
                      {supplementaryInsurancesLength === 0 && <span>{I18n.translate('general.none')}</span>}
                    </div>
                    {supplementaryInsurancesLength > 0 &&
                      Object.keys(props.supplementaryInsurances).map((supplementaryInsuranceKey) => {
                        const insuranceContent = props.quoteContent.components[supplementaryInsuranceKey]
                        let fieldKey = supplementaryInsuranceKey
                        if (
                          props.quoteContent.fieldNames[
                            `${supplementaryInsuranceKey}${props.selectedQuote.calculatorName}`
                          ]
                        ) {
                          fieldKey = `${supplementaryInsuranceKey}${props.selectedQuote.calculatorName}`
                        }
                        if (!insuranceContent) {
                          return null
                        }

                        return (
                          <div className="col-12 py-1" key={`car-summary-sup-insurance-${supplementaryInsuranceKey}`}>
                            <div className="row align-items-center h-100">
                              <div className="col-2 text-center">
                                {insuranceContent.icon && <span className={`${insuranceContent.icon} h1`} />}
                              </div>
                              <div className="col-4 pl-1">
                                <span>{props.quoteContent.fieldNames[fieldKey]}</span>
                              </div>
                              {Array.isArray(addedCoverages.getIn([supplementaryInsuranceKey, 'labels'])) ? (
                                <div className="col-6 pl-1">
                                  <span>
                                    {addedCoverages.getIn([
                                      supplementaryInsuranceKey,
                                      'labels',
                                      addedCoverages.getIn([supplementaryInsuranceKey, 'selectedIndex'], 0),
                                    ])}
                                  </span>
                                </div>
                              ) : (
                                addedCoverages.getIn([supplementaryInsuranceKey, 'variant', 'id']) && (
                                  <div className="col-6 pl-1">
                                    <span>
                                      {addedCoverages.getIn(
                                        [supplementaryInsuranceKey, 'variant', 'shortLabel'],
                                        addedCoverages.getIn([supplementaryInsuranceKey, 'variant', 'label'], ''),
                                      )}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>
            )}
            <hr className="border-0 border-sm-top" />
          </>
        )}
        <div className="row mt-1 align-items-center">
          <div className="col-12 col-lg-3 mb-3 mb-lg-0">
            <span>{I18n.translate('general.overallInsurance')}:</span>{' '}
            <strong className="lead">
              {paymentPeriod === 'annually' && I18n.translate('general.pricePerYear', { price })}
              {paymentPeriod === 'semi-annually' && I18n.translate('general.pricePerHalfYear', { price })}
              {paymentPeriod === 'quarterly' && I18n.translate('general.pricePerQuarterYear', { price })}
              {paymentPeriod === 'monthly' && I18n.translate('general.pricePerMonth', { price })}
            </strong>
          </div>
          {typeof props.insuranceStart !== 'string' && (
            <div className="col-10 col-lg-8 offset-lg-1 order-0 order-sm-0 text-sm-right mb-2 mb-sm-0 justify-content-center">
              <PatchSpecification
                content={props.content}
                contentPatch={{ fieldOrder: ['insuranceStart'] }}
                specification={props.specification}
              >
                <FieldForm
                  arrayTypes={{}}
                  blockClassName=""
                  data={props.userData}
                  debug={props.debug}
                  formData={props.userData}
                  formGroupClassName="form-group d-flex flex-row flex-wrap flex-sm-row align-items-center"
                  isBackOffice={props.isBackOffice}
                  onChangeValue={props.onChangeValue}
                  validationErrors={props.validationErrors}
                />
              </PatchSpecification>
            </div>
          )}
          {typeof props.insuranceStart === 'string' && (
            <div className="col-12 col-lg-6 offset-lg-3 order-0 order-sm-0 mb-2 mb-sm-0 justify-content-center">
              <span>{I18n.translate('general.insuranceStart')}:</span>{' '}
              <strong className="lead">{moment(props.insuranceStart).format('DD.MM.YYYY HH:mm')}</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

CarSummaryPanel.defaultProps = {
  supplementaryInsurances: {},
  templateVariables: {},
}

export default CarSummaryPanel
